import React, { useState, useEffect } from "react";
import { redirect, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import secureStorage from "../utils/secureStorage";
import { validateToken } from "../tokenValidation";

const SignUpForm = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 1899 },
    (_, i) => currentYear - i
  );
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    middleName: "",
    lastName: "",
    gender: "",
    dayOfBirth: "",
    monthOfBirth: "",
    yearOfBirth: "",
    addressLine: "",
    district: "",
    state: "",
    pinCode: "",
    phoneNumber: "",
    abhaAddress: "",
    abhaNumber: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const isFormValid = () => {
    return (
      formData.name.trim() !== "" &&
      formData.lastName.trim() !== "" &&
      formData.gender.trim() !== "" &&
      formData.dayOfBirth !== "" &&
      formData.monthOfBirth !== "" &&
      formData.yearOfBirth !== "" &&
      formData.phoneNumber.trim() !== ""
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = secureStorage.get("authToken");

    if (!isFormValid()) {
      toast.error("Please fill all mandatory fields.");
      return;
    }
    setIsSubmitting(true);
    // console.log(formData);
    try {
      const url = `${API_URL}hospital/register-user`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "X-HIP-ID": secureStorage.get("clinic"),
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Registration Successful!");
        // window.location.href = '/hospital-dashboard';
        setFormData({
          name: "",
          middleName: "",
          lastName: "",
          gender: "",
          dateOfBirth: null,
          addressLine: "",
          district: "",
          state: "",
          pinCode: "",
          phoneNumber: "",
          abhaAddress: "",
          abhaNumber: ""
        });
        setCurrentStep(1);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Registration failed.");
      }
    } catch (error) {
      console.error("Error during registration:", error);
      toast.error("An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };


  useEffect(() => {
    validateToken(navigate);
  }, [navigate]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            {/* Personal Details */}
            <div className="mb-4">
              <label
                htmlFor="name"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your first name"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="middleName"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                Middle Name <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="text"
                id="middleName"
                name="middleName"
                value={formData.middleName}
                onChange={handleChange}
                placeholder="Enter your middle name"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="lastName"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="gender"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-lg font-medium text-gray-800 mb-2">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-2">
                <select
                  name="dayOfBirth"
                  value={formData.dayOfBirth}
                  onChange={handleChange}
                  className="w-1/3 px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 text-gray-900 text-lg"
                >
                  <option value="">Day</option>
                  {days.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
                <select
                  name="monthOfBirth"
                  value={formData.monthOfBirth}
                  onChange={handleChange}
                  className="w-1/3 px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 text-gray-900 text-lg"
                >
                  <option value="">Month</option>
                  {months.map((month) => (
                    <option key={month} value={month}>
                      {month}
                    </option>
                  ))}
                </select>
                <select
                  name="yearOfBirth"
                  value={formData.yearOfBirth}
                  onChange={handleChange}
                  className="w-1/3 px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 text-gray-900 text-lg"
                >
                  <option value="">Year</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        );
      case 2:
        return (
          <>
            {/* Address Details */}
            <div className="mb-4">
              <label
                htmlFor="addressLine"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                Address Line
              </label>
              <input
                type="text"
                id="addressLine"
                name="addressLine"
                value={formData.addressLine}
                onChange={handleChange}
                placeholder="Enter your address"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="district"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                District
              </label>
              <input
                type="text"
                id="district"
                name="district"
                value={formData.district}
                onChange={handleChange}
                placeholder="Enter your district"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="state"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                State
              </label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="Enter your state"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="pinCode"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                Pin Code
              </label>
              <input
                type="text"
                id="pinCode"
                name="pinCode"
                value={formData.pinCode}
                onChange={handleChange}
                placeholder="Enter your pin code"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
          </>
        );
      case 3:
        return (
          <>
            {/* Contact & Additional Info */}
            <div className="mb-4">
              <label
                htmlFor="phoneNumber"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Enter your phone number"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="abhaAddress"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                ABHA Address <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="text"
                id="abhaAddress"
                name="abhaAddress"
                value={formData.abhaAddress}
                onChange={handleChange}
                placeholder="Enter your ABHA address"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="abhaNumber"
                className="block text-lg font-medium text-gray-800 mb-2"
              >
                ABHA Number <span className="text-gray-500">(Optional)</span>
              </label>
              <input
                type="text"
                id="abhaNumber"
                name="abhaNumber"
                value={formData.abhaNumber}
                onChange={handleChange}
                placeholder="Enter your ABHA number"
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 text-lg"
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-8">
        <h2 className="text-2xl font-bold text-teal-600 text-center mb-4">
          Register New Patient
        </h2>
        <p className="text-center text-gray-600 mb-6">
          Step {currentStep} of 3
        </p>
        <form onSubmit={handleSubmit}>
          {renderStep()}
          <div className="flex justify-between mt-6">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="px-6 py-3 rounded-lg bg-gray-300 text-gray-700 font-semibold hover:bg-gray-400 transition-colors"
              >
                Previous
              </button>
            )}
            {currentStep < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                className="ml-auto px-6 py-3 rounded-lg bg-teal-600 text-white font-semibold hover:bg-teal-700 transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={!isFormValid() || isSubmitting}
                className={`ml-auto px-6 py-3 rounded-lg font-semibold flex items-center justify-center transition-colors ${isFormValid() && !isSubmitting
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-gray-300 text-gray-600 cursor-not-allowed"
                  }`}
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "Submit"
                )}
              </button>
            )}
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default SignUpForm;
